import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createDistributorEmployee,
  deleteDistributorEmployee,
  getDistributorEmployeeById,
  listEmployeeRoles,
  listDistributorEmployees,
  resendDistributorEmployeeInvite,
  updateDistributorEmployee,
  updateEmployeeClientMapping,
  type ListDistributorEmployeesParams,
} from "../../api/distributor";
import type {
  CreateDistributorEmployeePayload,
  UpdateDistributorEmployeePayload,
  UpdateEmployeeClientMappingPayload,
} from "../../api/distributor/employees";

export const employeeKeys = {
  all: ["distributor-employees"] as const,
  list: (params?: ListDistributorEmployeesParams) =>
    [
      ...employeeKeys.all,
      {
        page: params?.page ?? null,
        page_size: params?.page_size ?? null,
        distributor: params?.distributor ?? null,
        role: params?.role ?? null,
        status: params?.status ?? null,
      },
    ] as const,
  byId: (id?: number) => ["distributor-employee-by-id", id ?? null] as const,
  roles: () => ["employee-roles"] as const,
  clients: () => ["clients"] as const,
  families: () => ["families"] as const,
};

export const distributorEmployeesQueryOptions = (
  params?: ListDistributorEmployeesParams,
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: employeeKeys.list(params),
    queryFn: () => listDistributorEmployees(params),
    enabled: options?.enabled ?? true,
  });

export const distributorEmployeeByIdQueryOptions = (
  id?: number,
  options?: { enabled?: boolean }
) =>
  queryOptions({
    queryKey: employeeKeys.byId(id),
    queryFn: () => getDistributorEmployeeById(id as number),
    enabled: (options?.enabled ?? true) && id != null,
  });

export const employeeRolesQueryOptions = (options?: { enabled?: boolean }) =>
  queryOptions({
    queryKey: employeeKeys.roles(),
    queryFn: () => listEmployeeRoles(),
    enabled: options?.enabled ?? true,
  });

export function useDistributorEmployees(
  params?: ListDistributorEmployeesParams,
  options?: { enabled?: boolean }
) {
  return useQuery(distributorEmployeesQueryOptions(params, options));
}

export function useInfiniteDistributorEmployees(
  params?: ListDistributorEmployeesParams,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: employeeKeys.list(params),
    queryFn: ({ pageParam }) =>
      listDistributorEmployees({
        ...params,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.next
        ? Number(lastPage.next.match(/page=(\d+)/)?.[1])
        : undefined;
    },
    initialPageParam: 1,
    enabled: options?.enabled ?? true,
  });
}

export function useCreateDistributorEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateDistributorEmployeePayload) =>
      createDistributorEmployee(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}

export function useDistributorEmployeeById(
  id?: number,
  options?: { enabled?: boolean }
) {
  return useQuery(distributorEmployeeByIdQueryOptions(id, options));
}

export function useEmployeeRoles(options?: { enabled?: boolean }) {
  return useQuery(employeeRolesQueryOptions(options));
}

export function useUpdateDistributorEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateDistributorEmployeePayload;
    }) => updateDistributorEmployee(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}

export function useDeleteDistributorEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteDistributorEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}

export function useResendDistributorEmployeeInvite() {
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateDistributorEmployeePayload;
    }) => resendDistributorEmployeeInvite(id, payload),
  });
}

export function useUpdateEmployeeClientMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
    }: {
      payload: UpdateEmployeeClientMappingPayload;
    }) => updateEmployeeClientMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.clients() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
    },
  });
}
